import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Download } from "lucide-react";

export const ImportDialog = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("update");

  const submit = async () => {
    if (!file) { toast.error("Please select an .xlsx file first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(`/purchases/import?mode=${mode}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(res.data.message || "Import successful");
      onImported?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to import file");
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/purchases/template`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111D38] border-slate-700 text-slate-100" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100 text-xl">Import Purchasing Monitoring Excel</DialogTitle>
          <DialogDescription className="text-slate-400">
            <strong className="text-slate-200">Update workflow:</strong> click <em>Download Excel Template</em> → edit in Excel/Sheets → upload again using <em>Update Data</em> mode. The system matches rows by Doc. Number + Product No. + PR Number.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
          <label data-testid="import-mode-update" className={`flex flex-col gap-1 rounded-lg border p-3 cursor-pointer transition-all ${mode === "update" ? "border-amber-500/60 bg-amber-500/10" : "border-slate-700 hover:border-slate-600"}`}>
            <div className="flex items-center gap-2">
              <input type="radio" checked={mode === "update"} onChange={() => setMode("update")} className="accent-amber-500" />
              <span className="text-sm font-semibold text-slate-100">Update Data</span>
            </div>
            <span className="text-[11px] text-slate-400 ml-6">Matching rows updated, new rows appended.</span>
          </label>
          <label data-testid="import-mode-append" className={`flex flex-col gap-1 rounded-lg border p-3 cursor-pointer transition-all ${mode === "append" ? "border-sky-500/60 bg-sky-500/10" : "border-slate-700 hover:border-slate-600"}`}>
            <div className="flex items-center gap-2">
              <input type="radio" checked={mode === "append"} onChange={() => setMode("append")} className="accent-sky-500" />
              <span className="text-sm font-semibold text-slate-100">Append Rows</span>
            </div>
            <span className="text-[11px] text-slate-400 ml-6">Duplicates skipped; only new rows inserted.</span>
          </label>
          <label data-testid="import-mode-replace" className={`flex flex-col gap-1 rounded-lg border p-3 cursor-pointer transition-all ${mode === "replace" ? "border-rose-500/60 bg-rose-500/10" : "border-slate-700 hover:border-slate-600"}`}>
            <div className="flex items-center gap-2">
              <input type="radio" checked={mode === "replace"} onChange={() => setMode("replace")} className="accent-rose-500" />
              <span className="text-sm font-semibold text-slate-100">Replace All</span>
            </div>
            <span className="text-[11px] text-slate-400 ml-6">Delete existing data, load from new file.</span>
          </label>
        </div>
        <label className="mt-2 flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed border-slate-600 bg-[#0B132B]/60 py-10 cursor-pointer hover:border-amber-500/60 hover:bg-amber-500/5 transition-all">
          <UploadCloud className="h-10 w-10 text-amber-400" />
          <div className="text-center">
            <div className="text-sm text-slate-200 font-medium">Drop .xlsx file or click to browse</div>
            {file && <div className="mt-1 text-xs text-amber-300 flex items-center gap-1"><FileSpreadsheet className="h-3.5 w-3.5" /> {file.name}</div>}
          </div>
          <input type="file" accept=".xlsx,.xls" hidden data-testid="input-import-file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <Button variant="ghost" onClick={downloadTemplate} data-testid="btn-download-template" className="text-amber-300 hover:text-amber-200 justify-start gap-2">
          <Download className="h-4 w-4" /> Download Excel Template
        </Button>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cancel</Button>
          <Button onClick={submit} disabled={busy || !file} data-testid="btn-do-import" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
            {busy ? "Importing..." : "Import Now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ExportDialog = ({ open, onOpenChange }) => {
  const [mode, setMode] = useState("cost_center");
  const doExport = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/purchases/export?mode=${mode}`, "_blank");
    toast.success("Export started — your file will download automatically");
    onOpenChange(false);
  };
  const options = [
    { v: "cost_center", t: "Multi-Sheet per Cost Center", d: "Each cost center gets its own tab (recommended)" },
    { v: "status", t: "Multi-Sheet per Status", d: "Separate tabs for FULL SUPPLY, WAITING SUPPLY, etc." },
    { v: "single", t: "Single Sheet Summary", d: "All data in one sheet with auto-filter enabled" },
  ];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111D38] border-slate-700 text-slate-100" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="text-slate-100 text-xl">Export Multi-Sheet Excel</DialogTitle>
          <DialogDescription className="text-slate-400">
            Excel file with amber headers, auto-filter, and freeze pane — ready for analysis.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 mt-2">
          {options.map((o) => (
            <label key={o.v} data-testid={`export-mode-${o.v}`} className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-all ${mode === o.v ? "border-amber-500/60 bg-amber-500/10" : "border-slate-700 hover:border-slate-600"}`}>
              <input type="radio" name="mode" checked={mode === o.v} onChange={() => setMode(o.v)} className="mt-1 accent-amber-500" />
              <div>
                <div className="font-semibold text-sm text-slate-100">{o.t}</div>
                <div className="text-xs text-slate-400 mt-0.5">{o.d}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300">Cancel</Button>
          <Button onClick={doExport} data-testid="btn-do-export" className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Download Excel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
